import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import { sortBy, get, isInteger } from 'lodash';
import { withRouter } from 'react-router';
import { withHandlers, withProps, withPropsOnChange } from 'recompose';
import { syncValidateData } from '../../../common/validator';
import BasicFormField from '../../../common/formFields/basicFormField';
import CancelButtonLink from '../../../common/buttons/cancelButtonLink';
import AdminSaveButton from '../../shared/Buttons/AdminSaveButton';
import AdminDeleteButton from '../../shared/Buttons/AdminDeleteButton';
import { getShops } from '../../shops/reducers';
import TextArea from '../../../common/textarea';
import TabbedForm from '../../../common/TabbedForm';
import { trimFunction } from '../../../common/trimData';
import { getStaffMembers } from '../../users/selectors';
import BrandShopList from './BrandShopList';
import BrandPageForm from './BrandPageForm';
import CustomAppForm from './CustomAppForm';
import { CurrencySelect } from '../../../common/formFields/CurrencySelect';
import CheckboxSwitch from '../../../common/checkboxSwitch';
import LanguageMultiSelectField from '../../../common/LanguageMultiSelect';
import SelectField from '../../../common/ModernSelect';
import { prepareLanguageSettings, preparePrimaryAccountOwner } from '../helpers';
import Tooltip from '../../../common/tooltip';
import KioskLogoControls from './KioskLogoControls';
import { OFFER_PAYOUT_OPTIONS } from '../constants';

const schema = Joi.object().keys({
  name: Joi.string().required().min(2).max(50)
    .label('Name'),
  website: Joi.string().min(2).max(50).allow('', null)
    .label('Website'),
  shopId: Joi.array()
    .optional(),
  email: Joi.string().email({ minDomainSegments: 2 }).required()
    .label('Email'),
  smsOriginator: Joi.string().optional().allow('', null)
    .label('SMS Originator #'),
  smsOriginatorVerified: Joi.boolean().optional().allow(null)
    .label('SMS Originator Verified'),
  rewardsEnabled: Joi.boolean().optional().allow(null),
  financingOffersDisabled: Joi.boolean().optional().allow(null),
  showBrandCustomersToBarbers: Joi.boolean().optional().allow(null),
  showBrandCustomersToShop: Joi.boolean().optional().allow(null),
  supportedLanguages: Joi.array(),
  settings: Joi.object().keys({
    ecommerceSyncIntegration: Joi.boolean().optional().allow(null),
    mailchimpIntegrationEnabled: Joi.boolean().optional().allow(null),
    membershipsEnabled: Joi.boolean().optional().allow(null),
    itemizedChargeDisplayEnabled: Joi.boolean().optional().allow(null),
    customKioskLogoEnabled: Joi.boolean().optional().allow(null),
    customKioskLogo: Joi.object()
      .when('customKioskLogoEnabled', {
        is: true,
        then: Joi.required().label('Custom kiosk logo'),
        otherwise: Joi.optional().allow(null),
      }),
    staffManagementEnabled: Joi.boolean().optional().allow(null),
    primaryAccountOwner: Joi.when('staffManagementEnabled', {
      is: Joi.boolean().valid(true).required(),
      then: Joi.alternatives().try(Joi.object(), Joi.string()).required().label('Primary account owner'),
      otherwise: Joi.object().optional().allow(null),
    }),
    displayReservationAds: Joi.boolean().optional().allow(null),
    offerInstantPayout: Joi.number().optional().allow(null),
  }).optional().allow(null),
});

const validate = values => syncValidateData(values, schema);

const renderCancelButton = () => (<CancelButtonLink link="/admin/brands" content="Cancel" />);

const renderSaveButton = () => (
  <AdminSaveButton content="Save" />
);

const renderDeleteButton = (onClick, brandShops) => () => {
  if (brandShops.length > 0) {
    return null;
  }
  return (<AdminDeleteButton
    className="c-btn c-btn--red u-width-120 u-mr- u-float-left"
    onClick={onClick}
  />);
};

const offerIpInputParseFn = (selected) => {
  const isRealOptionSelected = isInteger(get(selected, 'value')) && selected.value !== OFFER_PAYOUT_OPTIONS[0].value;
  return isRealOptionSelected ? selected.value : null;
};

const BrandForm = ({
  handleSubmit,
  initialValues,
  edit,
  onSave,
  onDelete,
  onSortChange,
  brandShops,
  extension,
  onMembershipsEnabledChange,
  onApiReaderEnabledChange,
  supportedLanguages,
  customKioskLogo,
  customKioskLogoEnabled,
  hasStaffManagementEnabled,
  possibleStaffMembers,
}) => (
  <TabbedForm
    className="c-inner-wrapper u-max-width-710 u-mt u-pt0"
    form="brandForm"
    initialValues={initialValues}
    validate={validate}
    onSubmit={handleSubmit(trimFunction(onSave))}
    deleteButton={renderDeleteButton(onDelete, brandShops)}
    cancelButton={renderCancelButton}
    saveButton={renderSaveButton}
    edit={edit}
  >
    <TabbedForm.Tab title="brand">
      <div className="c-heading c-heading--lightgrey c-wrapper u-p0 u-mt-- u-mb-" >Brand Settings</div>
      <BasicFormField
        placeholder="Name"
        label="Name"
        name="name"
        maxLength="255"
      />
      <div className="layout__item">
        <div className="c-label-new">Brand description</div>
        <TextArea className="c-textarea" name="description" placeholder="Brand description" />
      </div>
      <BasicFormField
        placeholder="Email"
        label="Email"
        name="email"
      />
      <BasicFormField
        placeholder="Website"
        label="Website"
        name="website"
      />
      <BasicFormField
        placeholder="Instagram"
        label="Instagram"
        name="instagram"
      />
      <div className="layout__item u-1/2-medium-and-up">
        <div className="c-label-new">Currency</div>
        <CurrencySelect name="currency" />
      </div>
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Language Settings</div>
      <LanguageMultiSelectField
        label="Supported Languages"
        name="settings.supportedLanguages"
      />
      <SelectField
        label="Brand default Language"
        name="settings.defaultLanguage"
        options={supportedLanguages}
      />
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">SMS Configuration</div>
      <BasicFormField
        placeholder="Number to send sms messages from"
        label="SMS Originator"
        name="smsOriginator"
        normalize={value => value === '' ? null : value}
      />
      <CheckboxSwitch
        name="smsOriginatorVerified"
        id="smsOriginatorVerified"
        label="SMS Originator Verified"
        wrapperClass="layout__item"
      />
      <div className="c-separator u-mb u-mt" />
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Options</div>
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.ecommerceSyncIntegration"
          id="settings.ecommerceSyncIntegration"
          label="Ecommerce integration"
          wrapperClass=""
        />
        <Tooltip
          className="c-info-tooltip-new--small"
          text="Enabling this setting allows this brand to link third party e-commerce platforms, such as Shopify, to their Commander product inventory, price, and more."
        />
      </div>
      <CheckboxSwitch
        name="settings.mailchimpIntegrationEnabled"
        id="settings.mailchimpIntegrationEnabled"
        label="Mailchimp integration"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="rewardsEnabled"
        id="rewardsEnabled"
        label="Loyalty program"
        wrapperClass="layout__item"
      />
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.staffManagementEnabled"
          id="settings.staffManagementEnabled"
          label="Staff management"
          wrapperClass=""
        />
        <Tooltip
          className="c-info-tooltip-new--small"
          text="Staff Management requires a primary account owner. The primary account owner always has all permissions. This user can grant Staff Management permissions to other brand and shop users"
        />
      </div>
      <SelectField
        display-if={hasStaffManagementEnabled}
        label="Primary account owner"
        name="settings.primaryAccountOwner"
        id="settings.primaryAccountOwner"
        options={possibleStaffMembers}
      />
      <CheckboxSwitch
        name="showBrandCustomersToShops"
        id="showBrandCustomersToShops"
        label="Show brand customers to shops"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="showBrandCustomersToBarbers"
        id="showBrandCustomersToBarbers"
        label="Show brand customers to barbers"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="overrideDefaultNotificationsConsent"
        id="overrideDefaultNotificationsConsent"
        label="Override default notifications consent for engage"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="financingOffersDisabled"
        id="financingOffersDisabled"
        label="Disable loans for brand"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="apiReaderEnabled"
        id="apiReaderEnabled"
        label="Enable API-based Readers"
        wrapperClass="layout__item"
        onChange={onApiReaderEnabledChange}
      />
      <CheckboxSwitch
        name="hideGiftcardCodeFromReceipt"
        id="hideGiftcardCodeFromReceipt"
        label="Hide giftcard codes from being displayed on client receipts"
        wrapperClass="layout__item"
      />
      <CheckboxSwitch
        name="settings.membershipsEnabled"
        id="settings.membershipsEnabled"
        label="Memberships"
        wrapperClass="layout__item"
        onChange={onMembershipsEnabledChange}
      />
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.itemizedChargeDisplayEnabled"
          id="settings.itemizedChargeDisplayEnabled"
          label="Itemized Charge Display on Kiosk"
        />
        <Tooltip
          className="c-info-tooltip-new"
          text="Enable this setting to display a client-facing charge list on iPad Kiosk during checkout. Requires user to have a Commander on Web + Commander on iPad Native setup with iPad in Kiosk mode."
        />
      </div>
      <div className="u-mb u-pl">
        <KioskLogoControls
          customKioskLogo={customKioskLogo}
          customKioskLogoEnabled={customKioskLogoEnabled}
        />
      </div>
      <div className="u-mb u-pl o-flexbox--justify-start o-flexbox o-flexbox--content-align-center">
        <CheckboxSwitch
          name="settings.displayReservationAds"
          id="settings.displayReservationAds"
          label="Ads on Reservation Page"
        />
        <Tooltip
          className="c-info-tooltip-new"
          text="Show Rokt ads on WOW reservation pages"
        />
      </div>
      <div>
        <div className="c-separator u-mb u-mt" />
        <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Rapid Transfer</div>
        <SelectField
          label={<>Offered After <Tooltip
            className="c-info-tooltip-new u-type--no-transform"
            text="The number of days and successful card transactions needed before offering Rapid Transfer to professionals. Default is 0 days/0 transactions."
          /></>}
          name="settings.offerInstantPayout"
          options={OFFER_PAYOUT_OPTIONS}
          format={(value) => ((value || value === 0) ? ({ value }) : OFFER_PAYOUT_OPTIONS[0])}
          parse={offerIpInputParseFn}
          defaultValue={OFFER_PAYOUT_OPTIONS[0]}
        />
        <div className="c-separator u-mb u-mt" />
      </div>
      <div>
        <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">Ad Conversion Tracking</div>

        <BasicFormField
          placeholder="Google Analytics Measurement Id"
          label="GA Measurement ID"
          name="settings.gaMeasurementId"
        />

        <BasicFormField
          placeholder="Google Analytics Access Token"
          label="GA Access Token"
          name="settings.gaMeasurementSecret"
        />

        <BasicFormField placeholder="Eg: 111122223333444" label="Meta pixel id" name="settings.metaPixelId" />

        <div className="c-separator u-mb u-mt" />
      </div>
      <div className="layout__item">
        <BrandShopList name="brandShops" brandShops={brandShops} onSortChange={onSortChange} />
      </div>
    </TabbedForm.Tab>
    <TabbedForm.Tab title="Web Brand Page" display-if={edit}>
      <BrandPageForm extension={extension || undefined} />
    </TabbedForm.Tab>
    <TabbedForm.Tab title="Custom App" display-if={edit}>
      <CustomAppForm extension={extension || undefined} />
    </TabbedForm.Tab>
  </TabbedForm>
);

BrandForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  edit: PropTypes.bool,
  shops: PropTypes.array, // eslint-disable-line
  brandShops: PropTypes.array, // eslint-disable-line
  extension: PropTypes.object, // eslint-disable-line
  onMembershipsEnabledChange: PropTypes.func.isRequired,
  onApiReaderEnabledChange: PropTypes.func.isRequired,
  customKioskLogo: PropTypes.bool.isRequired,
  customKioskLogoEnabled: PropTypes.bool.isRequired,
  hasStaffManagementEnabled: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  possibleStaffMembers: PropTypes.array.isRequired,
};

BrandForm.defaultProps = {
  edit: false,
};

const selector = formValueSelector('brandForm');
const mapStateToProps = state => ({
  shops: getShops(state),
  brandShops: sortBy(selector(state, 'brandShops'), ['shopOrder']),
  possibleStaffMembers: getStaffMembers(state),
  extension: selector(state, 'extension'),
  supportedLanguages: selector(state, 'settings.supportedLanguages'),
  defaultLanguage: selector(state, 'settings.defaultLanguage'),
  customKioskLogo: selector(state, 'settings.customKioskLogo'),
  customKioskLogoEnabled: selector(state, 'settings.customKioskLogoEnabled'),
  hasStaffManagementEnabled: selector(state, 'settings.staffManagementEnabled'),
  selectedPrimaryAccountOwnerOwner: selector(state, 'settings.primaryAccountOwner'),
});

const getPossiblePrimaryAccounts = (possibleStaffMembers, brandShops) => {
  if (!Array.isArray(possibleStaffMembers)) {
    return [];
  }

  if (brandShops.length === 1) {
    return possibleStaffMembers;
  }

  return possibleStaffMembers.filter(({ brandId }) => !!brandId);
};

const hasInitialPrimaryAccountOwnerChanged = (prevProps, nextProps) =>
  get(prevProps, 'initialValues.settings.primaryAccountOwner') !==
    get(nextProps, 'initialValues.settings.primaryAccountOwner');

const hasPossibleStaffMembersArrayLengthChanged = (prevProps, nextProps) =>
  get(prevProps, 'possibleStaffMembers.length') !== get(nextProps, 'possibleStaffMembers.length');

const hasSelectedPrimaryAccountOwnerChangedToString = (prevProps, nextProps) => (
  prevProps.selectedPrimaryAccountOwnerOwner !== nextProps.selectedPrimaryAccountOwnerOwner
  && typeof nextProps.selectedPrimaryAccountOwnerOwner === 'string'
);

export default compose(
  withRouter,
  connect(mapStateToProps),
  reduxForm({
    form: 'brandForm',
    validate,
  }),
  withProps(props => ({
    possibleStaffMembers: getPossiblePrimaryAccounts(props.possibleStaffMembers, props.brandShops)
      .map(({ id, username }) => ({
        label: username,
        value: id,
      })),
  })),
  withPropsOnChange(
    (prevProps, nextProps) =>
      // eslint-disable-next-line max-len
      hasInitialPrimaryAccountOwnerChanged(prevProps, nextProps)
      || hasPossibleStaffMembersArrayLengthChanged(prevProps, nextProps)
      || hasSelectedPrimaryAccountOwnerChangedToString(prevProps, nextProps),

    (props) => {
      if (props.initialValues.settings.primaryAccountOwner) {
        const accountOwner = props.possibleStaffMembers
          .find(({ value }) => value === props.initialValues.settings.primaryAccountOwner);

        if (accountOwner) {
          props.change('settings.primaryAccountOwner', accountOwner);
        }
      }
    },
  ),
  withPropsOnChange(['supportedLanguages'], (props) => {
    if (!(props.supportedLanguages || []).includes(props.defaultLanguage)) {
      props.change('settings.defaultLanguage', (props.supportedLanguages || [])[0]);
    }
  }),
  withHandlers({
    onSave: props => (data) => {
      return props.onSave(preparePrimaryAccountOwner(prepareLanguageSettings(data)));
    },
  }),
)(BrandForm);
